import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const apiClient = axios.create({ baseURL: `${API_URL}/api` });

// Token interceptor
apiClient.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem('lbu_admin_token');
  const userToken = localStorage.getItem('rtb_user_token');
  const token = config.url?.startsWith('/admin') ? adminToken : (userToken || adminToken);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============ PUBLIC ============
export const getPublicSettings = async () => {
  const response = await apiClient.get('/settings/public');
  return response.data;
};

export const getQuestionnaire = async () => {
  const response = await apiClient.get('/questionnaire');
  return response.data;
};

export const getCarriers = async () => {
  const response = await apiClient.get('/carriers');
  return response.data;
};

// ============ PARTICIPANT AUTH ============
export const registerParticipant = async (data) => {
  const response = await apiClient.post('/participant/register', data);
  return response.data;
};

export const loginParticipant = async (data) => {
  const response = await apiClient.post('/participant/login', data);
  return response.data;
};

export const forgotPassword = async (email) => {
  const response = await apiClient.post('/participant/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, password) => {
  const response = await apiClient.post('/participant/reset-password', { token, password });
  return response.data;
};

export const getMyProfile = async () => {
  const response = await apiClient.get('/participant/me');
  return response.data;
};

// ============ PARTICIPANT ACTIONS ============
export const submitQuestionnaire = async (answers) => {
  const response = await apiClient.post('/participant/questionnaire', { answers });
  return response.data;
};

export const confirmPayment = async (confirmationCode) => {
  const response = await apiClient.post('/participant/confirm-payment', { confirmation_code: confirmationCode });
  return response.data;
};

export const signContract = async (signature) => {
  const response = await apiClient.post('/participant/sign-contract', { signature });
  return response.data;
};

export const uploadIdDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/participant/upload-id', formData);
  return response.data;
};

export const completeOnboarding = async () => {
  const response = await apiClient.post('/participant/complete-onboarding');
  return response.data;
};

export const completeDay = async (day) => {
  const response = await apiClient.post(`/participant/complete-day/${day}`);
  return response.data;
};

// ============ LLC ============
export const getLLCStates = async () => {
  const response = await apiClient.get('/participant/llc/states');
  return response.data;
};

export const getLLCGuide = async (stateCode) => {
  const response = await apiClient.get(`/participant/llc/guide/${stateCode}`);
  return response.data;
};

export const setupLLC = async (data) => {
  const response = await apiClient.post('/participant/llc/setup', data);
  return response.data;
};

export const generateHardshipLetter = async (data) => {
  const response = await apiClient.post('/participant/llc/hardship-letter', data);
  return response.data;
};

export const getHardshipLetter = async (id) => {
  const response = await apiClient.get(`/participant/llc/hardship-letter/${id}`);
  return response.data;
};

// ============ OPERATING AGREEMENT & EIN ============
export const generateOperatingAgreement = async () => {
  const response = await apiClient.post('/participant/llc/operating-agreement');
  return response.data;
};

export const getOperatingAgreement = async (id) => {
  const response = await apiClient.get(`/participant/llc/operating-agreement/${id}`);
  return response.data;
};

export const generateEINGuide = async () => {
  const response = await apiClient.post('/participant/llc/ein-guide');
  return response.data;
};

// ============ ADMIN ============
export const adminLogin = async (data) => {
  const response = await apiClient.post('/admin/login', data);
  return response.data;
};

export const getStats = async () => {
  const response = await apiClient.get('/admin/stats');
  return response.data;
};

export const getSettings = async () => {
  const response = await apiClient.get('/admin/settings');
  return response.data;
};

export const updateSettings = async (data) => {
  const response = await apiClient.put('/admin/settings', data);
  return response.data;
};

export const getLeads = async (params) => {
  const response = await apiClient.get('/admin/leads', { params });
  return response.data;
};

export const deleteLead = async (id) => {
  const response = await apiClient.delete(`/admin/leads/${id}`);
  return response.data;
};

export const updateLead = async (id, data) => {
  const response = await apiClient.put(`/admin/leads/${id}`, data);
  return response.data;
};

export const createManualLead = async (data) => {
  const response = await apiClient.post('/admin/leads/manual', data);
  return response.data;
};

export const getParticipants = async () => {
  const response = await apiClient.get('/admin/participants');
  return response.data;
};

export const getParticipant = async (id) => {
  const response = await apiClient.get(`/admin/participants/${id}`);
  return response.data;
};

// ============ ADMIN TEAM ============
export const getAdminUsers = async () => {
  const response = await apiClient.get('/admin/team');
  return response.data;
};

export const createAdminUser = async (data) => {
  const response = await apiClient.post('/admin/team', data);
  return response.data;
};

export const deleteAdminUser = async (id) => {
  const response = await apiClient.delete(`/admin/team/${id}`);
  return response.data;
};

// ============ ADMIN ACTIVITY ============
export const getAdminActivity = async () => {
  const response = await apiClient.get('/admin/activity');
  return response.data;
};

// ============ PAYMENTS ============
export const createMastermindCheckout = async (originUrl) => {
  const response = await apiClient.post('/payments/create-checkout', { origin_url: originUrl });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await apiClient.get(`/payments/status/${sessionId}`);
  return response.data;
};

// ============ VIDEO GENERATION ============
export const generateVideo = async (data) => {
  const response = await apiClient.post('/admin/video/generate', data);
  return response.data;
};

export const getAdminVideos = async () => {
  const response = await apiClient.get('/admin/videos');
  return response.data;
};

// ============ EMAIL INBOX ============
export const getEmails = async (status = null) => {
  const params = status ? { status } : {};
  const response = await apiClient.get('/admin/emails', { params });
  return response.data;
};

export const getEmail = async (id) => {
  const response = await apiClient.get(`/admin/emails/${id}`);
  return response.data;
};

export const sendEmailResponse = async (id, responseText) => {
  const response = await apiClient.post(`/admin/emails/${id}/send`, { response: responseText });
  return response.data;
};

export const updateEmailDraft = async (id, draft) => {
  const response = await apiClient.put(`/admin/emails/${id}/draft`, { draft });
  return response.data;
};

export const archiveEmail = async (id) => {
  const response = await apiClient.delete(`/admin/emails/${id}`);
  return response.data;
};

export const regenerateAiDraft = async (id) => {
  const response = await apiClient.post(`/admin/emails/${id}/regenerate`);
  return response.data;
};

// ============ EMAIL ANALYTICS ============
export const getEmailAnalytics = async () => {
  const response = await apiClient.get('/admin/email-analytics');
  return response.data;
};

// ============ TESTIMONIALS (HeyGen) ============
export const generateTestimonial = async (data) => {
  const response = await apiClient.post('/admin/testimonials/generate', data);
  return response.data;
};

export const getTestimonialStatus = async (id) => {
  const response = await apiClient.get(`/admin/testimonials/${id}/status`);
  return response.data;
};

export const getTestimonials = async () => {
  const response = await apiClient.get('/admin/testimonials');
  return response.data;
};

// ============ SOCIAL LEADS ============
export const createSocialLead = async (data) => {
  const response = await apiClient.post('/leads/social', data);
  return response.data;
};

// ============ BACKGROUND CHECKS ============
export const getMyBackgroundCheck = async () => {
  const response = await apiClient.get('/participant/background-check');
  return response.data;
};

export const initiateBackgroundCheck = async () => {
  const response = await apiClient.post('/participant/background-check/initiate');
  return response.data;
};

export const getAdminBackgroundChecks = async (status = null) => {
  const params = status ? { status } : {};
  const response = await apiClient.get('/admin/background-checks', { params });
  return response.data;
};

export const getBackgroundCheckPackages = async () => {
  const response = await apiClient.get('/admin/background-checks/packages');
  return response.data;
};

// Legacy compatibility
export const getLead = async (id) => {
  const response = await apiClient.get(`/admin/leads/${id}`);
  return response.data;
};

export default apiClient;
